import { lazy, Suspense, useEffect } from 'react';

import { Navigate, Route, Outlet, createRoutesFromElements, ScrollRestoration, useNavigate } from 'react-router-dom';

import ContactBlacklistPage from '#contactListFeature/pages/ContactBlacklistPage/ContactBlacklistPage';
import ContactListDetailPage from '#contactListFeature/pages/ContactListDetailPage/ContactListDetailPage';
import ContactListsOverviewPage from '#contactListFeature/pages/ContactListsOverviewPage/ContactListsOverviewPage';
import { EmailCampaignsProvider } from '#emailCampaignFeature/contexts/EmailCampaignsContext';
import ProtectedAppRoute from '#globalComponents/ProtectedAppRoute/ProtectedAppRoute';
import { useAuth } from '#globalContexts/AuthProvider';
import { SubscriptionSuccessPage } from '#subscriptionsFeature/pages/SubscriptionSuccessPage/SubscriptionSuccessPage';
import SupportPage from '#supportFeature/pages/SupportPage/SupportPage';

import ConfirmPasswordResetPage from '../features/auth/pages/ConfirmPasswordResetPage/ConfirmPasswordResetPage';
import EmailConfirmationPage from '../features/auth/pages/EmailConfirmationPage/EmailConfirmationPage';
import LoginPage from '../features/auth/pages/LoginPage/LoginPage';
import RegisterPage from '../features/auth/pages/RegisterPage/RegisterPage';
import RequestPasswordResetPage from '../features/auth/pages/ResetPasswordPage/RequestPasswordResetPage';
import AircallOAuthCallbackPage from '../features/crm/pages/AircallOAuthCallbackPage/AircallOAuthCallbackPage';
import CalendarOAuthCallbackPage from '../features/crm/pages/CalendarOAuthCallbackPage/CalendarOAuthCallbackPage';
import CrmActivitiesPage from '../features/crm/pages/CrmActivitiesPage/CrmActivitiesPage';
import CrmBundleDetailPage from '../features/crm/pages/CrmBundleDetailPage/CrmBundleDetailPage';
import CrmCalendarPage from '../features/crm/pages/CrmCalendarPage/CrmCalendarPage';
import CrmCompaniesPage from '../features/crm/pages/CrmCompaniesPage/CrmCompaniesPage';
import CrmCompanyPage from '../features/crm/pages/CrmCompanyPage/CrmCompanyPage';
import CrmOnboardingPage from '../features/crm/pages/CrmOnboardingPage/CrmOnboardingPage';
import CrmPipelineBoardPage from '../features/crm/pages/CrmPipelineBoardPage/CrmPipelineBoardPage';
import CrmProductDetailPage from '../features/crm/pages/CrmProductDetailPage/CrmProductDetailPage';
import CrmProductsPage from '../features/crm/pages/CrmProductsPage/CrmProductsPage';
import CrmSettingsPage from '../features/crm/pages/CrmSettingsPage/CrmSettingsPage';
import CrmShell from '../features/crm/pages/CrmShell/CrmShell';
import CrmTrashPage from '../features/crm/pages/CrmTrashPage/CrmTrashPage';
import EmailTemplatesPage from '../features/emailCampaigns/pages/EmailTemplatesPage/EmailTemplatesPage';
import MailCampaignsOverviewPage from '../features/emailCampaigns/pages/MailCampaignsOverviewPage/MailCampaignsOverviewPage';
import MailCampaignsDetailsPage from '../features/emailCampaigns/pages/MailCampaingsDetailsPage/MailCampaignsDetailsPage';
import CompanyDetailPage from '../features/leadOverview/pages/CompanyDetailPage/CompanyDetailPage';
import CompanySearchPage from '../features/leadOverview/pages/CompanySearchPage/CompanySearchPage';
import DashboardPage from '../features/leadOverview/pages/DashboardPage/DashboardPage';
import LeadOverviewPage from '../features/leadOverview/pages/LeadOverviewPage/LeadOverviewPage';
import MapSearchPage from '../features/mapSearch/pages/MapSearchPage/MapSearchPage';
import { PostMailingCheckoutCancelPage } from '../features/postMailing/pages/PostMailingCheckoutReturnPage/PostMailingCheckoutCancelPage';
import { PostMailingCheckoutSuccessPage } from '../features/postMailing/pages/PostMailingCheckoutReturnPage/PostMailingCheckoutSuccessPage';
import PostMailingDetailsPage from '../features/postMailing/pages/PostMailingDetailsPage/PostMailingDetailsPage';
import PostMailingOverviewPage from '../features/postMailing/pages/PostMailingOverviewPage/PostMailingOverviewPage';
import PostMailingTemplatesPage from '../features/postMailing/pages/PostMailingTemplatesPage/PostMailingTemplatesPage';
import OAuthCallbackPage from '../features/settings/pages/OAuthCallbackPage/OAuthCallbackPage';
import SettingsPage from '../features/settings/pages/SettingsPage/SettingsPage';
import NotAllowedPage from '../globalComponents/NotAllowedPage/NotAllowedPage';
import ProtectedRoute from '../globalComponents/ProtectedRoute';
import AuthShell from '../layouts/AuthShell';

const PostMailingTemplateEditorPage = lazy(
	() => import('../features/postMailing/pages/PostMailingTemplateEditorPage/PostMailingTemplateEditorPage'),
);

// Lazy on purpose: internal support tooling — customers never download this chunk.
const SupportAccountsPage = lazy(() => import('../features/admin/pages/SupportAccountsPage/SupportAccountsPage'));
const SupportAccountPage = lazy(() => import('../features/admin/pages/SupportAccountPage/SupportAccountPage'));
const SupportMergeReviewPage = lazy(
	() => import('../features/admin/pages/SupportMergeReviewPage/SupportMergeReviewPage'),
);
const SupportSharedHostsPage = lazy(
	() => import('../features/admin/pages/SupportSharedHostsPage/SupportSharedHostsPage'),
);

/* Helper to redirect to dashboard or login based on auth status */
const RootRedirect = () => {
	const { user, loading } = useAuth();
	const navigate = useNavigate();

	useEffect(() => {
		if (!loading) {
			navigate(user ? '/dashboard' : '/login', { replace: true });
		}
	}, [user, loading, navigate]);

	return null;
};

/* Root layout with ScrollRestoration */
const RootLayout = () => {
	return (
		<>
			<ScrollRestoration />
			<Suspense fallback={<div className="p-4">Lade&nbsp;…</div>}>
				<Outlet />
			</Suspense>
		</>
	);
};

export function createAppRoutes() {
	return createRoutesFromElements(
		<Route element={<RootLayout />}>
			{/* Root path with conditional navigation */}
			<Route path="/" element={<RootRedirect />} />

			{/* Email confirmation - no layout, direct processing */}
			<Route path="auth/confirm" element={<EmailConfirmationPage />} />

			{/* OAuth callback - no layout, direct processing */}
			<Route path="oauth/callback" element={<OAuthCallbackPage />} />
			<Route path="integrations/aircall/callback" element={<AircallOAuthCallbackPage />} />
			<Route path="integrations/calendar/callback" element={<CalendarOAuthCallbackPage />} />

			{/* Public auth routes */}
			<Route element={<AuthShell />}>
				<Route path="login" element={<LoginPage />} />
				<Route path="register" element={<RegisterPage />} />
				<Route path="forgot-password" element={<RequestPasswordResetPage />} />
				<Route path="reset-password" element={<ConfirmPasswordResetPage />} />
			</Route>

			{/* Checkout return - requires auth but no subscription */}
			<Route element={<ProtectedRoute />}>
				<Route path="subscription-success" element={<SubscriptionSuccessPage />} />
			</Route>

			{/* Protected area - requires auth (capabilities gate individual features) */}
			<Route element={<ProtectedAppRoute />}>
				<Route path="dashboard" element={<DashboardPage />} />
				<Route path="leads" element={<LeadOverviewPage />} />
				<Route path="leads/company/:id" element={<CompanyDetailPage />} />
				<Route path="company-details" element={<CompanySearchPage />} />
				<Route path="map-search" element={<MapSearchPage />} />
				<Route path="settings" element={<SettingsPage />} />
				<Route path="support" element={<SupportPage />} />
				<Route path="contact-lists" element={<ContactListsOverviewPage />} />
				<Route path="contact-lists/blacklist" element={<ContactBlacklistPage />} />
				<Route path="contact-lists/:listId" element={<ContactListDetailPage />} />
				<Route
					path="admin/accounts"
					element={
						<Suspense fallback={null}>
							<SupportAccountsPage />
						</Suspense>
					}
				/>
				<Route
					path="admin/accounts/:orgId"
					element={
						<Suspense fallback={null}>
							<SupportAccountPage />
						</Suspense>
					}
				/>
				<Route
					path="admin/data-quality/merges"
					element={
						<Suspense fallback={null}>
							<SupportMergeReviewPage />
						</Suspense>
					}
				/>
				<Route
					path="admin/data-quality/shared-hosts"
					element={
						<Suspense fallback={null}>
							<SupportSharedHostsPage />
						</Suspense>
					}
				/>
			</Route>

			{/* Protected area with email.campaigns capability */}
			<Route element={<ProtectedAppRoute required="email.campaigns" />}>
				<Route
					path="email-campaigns"
					element={
						<EmailCampaignsProvider>
							<MailCampaignsOverviewPage />
						</EmailCampaignsProvider>
					}
				/>
				<Route path="/email-campaigns/:id" element={<MailCampaignsDetailsPage />} />
				<Route path="email-campaigns/templates" element={<EmailTemplatesPage />} />
			</Route>

			{/* Protected area with postmailing.campaigns capability */}
			<Route element={<ProtectedAppRoute required="postmailing.campaigns" />}>
				<Route path="post-mailing" element={<PostMailingOverviewPage />} />
				{/* Stripe Checkout return URLs for post-mailing payment (success / cancel). */}
				<Route path="post-mailing/checkout-success" element={<PostMailingCheckoutSuccessPage />} />
				<Route path="post-mailing/checkout-cancel" element={<PostMailingCheckoutCancelPage />} />
				<Route path="post-mailing/:id" element={<PostMailingDetailsPage />} />
				<Route path="post-mailing/templates" element={<PostMailingTemplatesPage />} />
				<Route
					path="post-mailing/templates/:templateId"
					element={
						<Suspense fallback={null}>
							<PostMailingTemplateEditorPage />
						</Suspense>
					}
				/>
			</Route>

			{/* Protected area with crm.access capability.
			    The dashboard lives at the global /dashboard route (hosts both
			    classic + KI-Vertriebsleiter), so /crm/* only has pipeline-level tabs. */}
			<Route element={<ProtectedAppRoute required="crm.access" />}>
				<Route path="crm" element={<CrmShell />}>
					<Route index element={<Navigate to="/crm/pipeline" replace />} />
					<Route path="onboarding" element={<CrmOnboardingPage />} />
					<Route path="dashboard" element={<Navigate to="/dashboard" replace />} />
					<Route path="pipeline" element={<CrmPipelineBoardPage />} />
					<Route path="pipeline/:pipelineId" element={<CrmPipelineBoardPage />} />
					<Route path="companies" element={<CrmCompaniesPage />} />
					<Route path="companies/:companyId" element={<CrmCompanyPage />} />
					<Route path="trash" element={<CrmTrashPage />} />
					<Route path="activities" element={<CrmActivitiesPage />} />
					<Route path="calendar" element={<CrmCalendarPage />} />
					<Route path="products" element={<CrmProductsPage />} />
					<Route path="products/:productId" element={<CrmProductDetailPage />} />
					<Route path="products/bundles/:bundleId" element={<CrmBundleDetailPage />} />
					<Route path="settings" element={<CrmSettingsPage />} />
				</Route>
			</Route>

			<Route path="not-allowed" element={<NotAllowedPage />} />

			{/* Fallback */}
			<Route path="*" element={<Navigate to="/" replace />} />
		</Route>,
	);
}
